import React, { useState } from "react";
import dayjs, { Dayjs } from "dayjs";
import { Box, Grid, Typography } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import LoadingButton from "@mui/lab/LoadingButton";
import { defaultStyle } from "components/Modals";
import { addPortfolioContract } from "api/resources/contracts";
import { Snackbar } from "types/snackbar";
import { fetchPortfolioContracts } from "store/appSlice";
import { useAppDispatch } from "hooks";

interface AddPortfolioContractModalProps {
  clientId: number;
  setOpenModal: React.Dispatch<React.SetStateAction<boolean>>;
  setSnackbar: React.Dispatch<React.SetStateAction<Snackbar>>;
}

const AddPortfolioContractModal = ({
  clientId,
  setOpenModal,
  setSnackbar,
}: AddPortfolioContractModalProps) => {
  const [loading, setLoading] = useState(false);
  const [startDate, setStartDate] = useState<Dayjs | null>(dayjs());
  const [endDate, setEndDate] = useState<Dayjs | null>(dayjs());
  const dispatch = useAppDispatch();

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setLoading(true);

    const postData = {
      clientId: clientId,
      validFrom: dayjs(startDate).format("YYYY-MM-DD").toString(),
      validTo: dayjs(endDate).format("YYYY-MM-DD").toString(),
    };

    addPortfolioContract(postData)
      .then(() => {
        setLoading(false);
        setOpenModal(false);
        dispatch(fetchPortfolioContracts(clientId));
        setSnackbar({
          open: true,
          severity: "success",
          message: "Portfolio contract entered successfully",
        });
      })
      .catch((error) => {
        setSnackbar({
          open: true,
          severity: "error",
          message: error.response?.data?.message,
        });
        setLoading(false);
        setOpenModal(false);
      });
  };

  return (
    <Box sx={defaultStyle}>
      <Typography id="modal-modal-title" variant="h5" component="h5">
        Enter a new portfolio contract
      </Typography>
      <Box component="form" onSubmit={handleSubmit} noValidate>
        <Grid container spacing={2} sx={{ mt: "1rem", mb: "2rem" }}>
          <Grid item sm={6}>
            <DatePicker
              label="Start date"
              value={startDate}
              onChange={(newValue) => setStartDate(newValue)}
              slotProps={{ textField: { fullWidth: true, size: "small" } }}
            />
          </Grid>
          <Grid item sm={6}>
            <DatePicker
              label="End date"
              value={endDate}
              onChange={(newValue) => setEndDate(newValue)}
              slotProps={{ textField: { fullWidth: true, size: "small" } }}
            />
          </Grid>
          <Grid item sm={12} sx={{ mt: "1rem" }}>
            <LoadingButton
              type="submit"
              variant="contained"
              loading={loading}
              loadingIndicator="Sending..."
              disabled={loading}
            >
              Enter contract
            </LoadingButton>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default AddPortfolioContractModal;
